import type { JSX } from 'react';
import { Table, Text } from '@mantine/core';
import { feeRows } from '@pst/shared';
import type { TimeCategory, TimeCategoryInput } from '../../api';
import { TOTAL_LABEL, formatAmount, formatHours } from '../../format';
import { AmountCell } from '../../ui/AmountCell';
import { Section } from '../../ui/Section';

/**
 * Honorarstand (AP-107), for project management: per position the agreed
 * fee, how far the work is, what has been billed - and what is open as the
 * base of an interim invoice. PST writes no invoices (A-51); the CSV goes to
 * whoever does the bookkeeping.
 */
export function TimeFees({ categories, onChange }: {
  categories: readonly TimeCategory[];
  onChange: (id: string, body: TimeCategoryInput) => void;
}): JSX.Element {
  const { rows, total } = feeRows(categories);
  const clamp = (v: number | null, max: number): number | null => (v === null ? null : Math.min(max, Math.max(0, v)));
  return (
    <Section
      title="Honorarstand"
      description="Grundlage für Abschlagsrechnungen: Honorar netto je Position, Leistungsstand, bereits abgerechnet. Keine Rechnung – die Rechnung schreibt die Buchhaltung. Nur für die Projektleitung sichtbar; Export über „Herunterladen“."
    >
      <Table.ScrollContainer minWidth={760}>
        <Table verticalSpacing={2}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={110}>Kategorie</Table.Th>
              <Table.Th>Bezeichnung</Table.Th>
              <Table.Th w={130} ta="right">Honorar netto</Table.Th>
              <Table.Th w={110} ta="right">Leistungsstand %</Table.Th>
              <Table.Th w={120} ta="right">erbracht</Table.Th>
              <Table.Th w={130} ta="right">abgerechnet</Table.Th>
              <Table.Th w={120} ta="right">offen</Table.Th>
              <Table.Th w={110} ta="right" title="gebucht zu Budget">Stunden</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r) => (
              <Table.Tr key={r.id ?? r.code}>
                <Table.Td><Text size="sm" ff="monospace" fw={600}>{r.code}</Text></Table.Td>
                <Table.Td><Text size="sm" lineClamp={1}>{r.label}</Text></Table.Td>
                <Table.Td><AmountCell value={r.fee} placeholder="—" onCommit={(fee) => r.id && onChange(r.id, { fee: clamp(fee, 1_000_000_000) })} /></Table.Td>
                <Table.Td><AmountCell value={r.progress} digits={r.progress !== null && !Number.isInteger(r.progress) ? 1 : 0} placeholder="—" onCommit={(progress) => r.id && onChange(r.id, { progress: progress === null ? null : Math.round(clamp(progress, 100)! * 10) / 10 })} /></Table.Td>
                <Table.Td ta="right"><Text size="sm">{r.earned === null ? '' : formatAmount(r.earned)}</Text></Table.Td>
                <Table.Td><AmountCell value={r.billed === 0 ? null : r.billed} placeholder="—" onCommit={(billed) => r.id && onChange(r.id, { billed: clamp(billed, 1_000_000_000) })} /></Table.Td>
                <Table.Td ta="right"><Text size="sm" fw={600} c={r.open !== null && r.open < 0 ? 'red.7' : undefined}>{r.open === null ? '' : formatAmount(r.open)}</Text></Table.Td>
                <Table.Td ta="right"><Text size="xs" c="dimmed">{formatHours(r.booked)}{r.budgetHours !== null ? ` / ${formatHours(r.budgetHours)}` : ''}</Text></Table.Td>
              </Table.Tr>
            ))}
            <Table.Tr className="pst-total-row">
              <Table.Td colSpan={2}><Text size="sm" fw={700}>{TOTAL_LABEL}</Text></Table.Td>
              <Table.Td ta="right"><Text size="sm" fw={700}>{formatAmount(total.fee)}</Text></Table.Td>
              <Table.Td />
              <Table.Td ta="right"><Text size="sm" fw={700}>{formatAmount(total.earned)}</Text></Table.Td>
              <Table.Td ta="right"><Text size="sm" fw={700}>{formatAmount(total.billed)}</Text></Table.Td>
              <Table.Td ta="right"><Text size="sm" fw={700}>{formatAmount(total.open)}</Text></Table.Td>
              <Table.Td />
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Text size="xs" c="dimmed">Beträge in Euro netto. Den Leistungsstand schätzt die Projektleitung; die gebuchten Stunden daneben sind nur ein Anhalt.</Text>
    </Section>
  );
}
